import { Head, Link, router } from "@inertiajs/react"
import { ArrowLeft, Plus, Trash2 } from "lucide-react"
import { useState } from "react"

import { Button } from "@/components/ui/button"
import { DataTable, Td, Th, Thead, Tr } from "@/components/steel/table"
import { Field, LinkAction, PageHeader } from "@/components/steel"
import { Input } from "@/components/ui/input"
import { Textarea } from "@/components/ui/textarea"
import { formatCurrency } from "@/lib/utils"

type CandidateLine = {
  amount: number
  description: string
  kind: string
  quantity: number
  sourceId: number | null
  sourceType: string | null
  unitAmount: number
}

type DraftLine = {
  description: string
  kind: string
  quantity: string
  sourceId: number | null
  sourceType: string | null
  unitAmount: string
}

type Props = {
  candidateLines: CandidateLine[]
  defaultTermsDays: number
  from?: string | null
  project: { id: string; name: string; client: string }
  to?: string | null
}

function toDraft(line: CandidateLine): DraftLine {
  return {
    description: line.description,
    kind: line.kind,
    quantity: String(line.quantity),
    sourceId: line.sourceId,
    sourceType: line.sourceType,
    unitAmount: String(line.unitAmount),
  }
}

export default function NewInvoice({ candidateLines, defaultTermsDays, from, project, to }: Props) {
  const [lines, setLines] = useState<DraftLine[]>(candidateLines.map(toDraft))
  const [periodStart, setPeriodStart] = useState(from ?? "")
  const [periodEnd, setPeriodEnd] = useState(to ?? "")
  const [tax, setTax] = useState("0")
  const [notes, setNotes] = useState("")
  const [termsDays, setTermsDays] = useState(String(defaultTermsDays))

  const updateLine = (index: number, patch: Partial<DraftLine>) =>
    setLines((prev) => prev.map((l, i) => (i === index ? { ...l, ...patch } : l)))

  const lineAmount = (line: DraftLine) => (Number(line.quantity) || 0) * (Number(line.unitAmount) || 0)
  const subtotal = lines.reduce((sum, l) => sum + lineAmount(l), 0)
  const total = subtotal + (Number(tax) || 0)
  const laborTotal = lines.filter((l) => l.kind === "labor").reduce((sum, l) => sum + lineAmount(l), 0)
  const expenseTotal = lines.filter((l) => l.kind === "expense").reduce((sum, l) => sum + lineAmount(l), 0)

  const submit = () => {
    router.post(`/projects/${project.id}/invoices`, {
      dueDate: "",
      issueDate: "",
      lines: lines.map((l) => ({
        amount: lineAmount(l),
        description: l.description,
        kind: l.kind,
        quantity: l.quantity,
        sourceId: l.sourceId,
        sourceType: l.sourceType,
        unitAmount: l.unitAmount,
      })),
      notes,
      periodEnd,
      periodStart,
      tax,
      termsDays,
    })
  }

  return (
    <>
      <Head title="New invoice" />

      <PageHeader
        leading={
          <Button variant="ghost" size="icon-sm" asChild>
            <Link href={`/projects/${project.id}`}>
              <ArrowLeft />
            </Link>
          </Button>
        }
        title="New invoice"
        meta={`${project.name} · ${project.client}`}
      />

      <div className="grid xl:grid-cols-[minmax(0,1fr)_296px]">
        <div className="border-border px-6 py-4 pb-7 xl:border-r">
          <div className="mb-5 flex items-end gap-2.5">
            <Field label="Labor from" className="w-[150px]">
              <Input type="date" value={periodStart} onChange={(e) => setPeriodStart(e.target.value)} />
            </Field>
            <Field label="To" className="w-[150px]">
              <Input type="date" value={periodEnd} onChange={(e) => setPeriodEnd(e.target.value)} />
            </Field>
            <Button
              variant="outline"
              size="sm"
              onClick={() =>
                router.get(
                  `/projects/${project.id}/invoices/new`,
                  { from: periodStart, to: periodEnd },
                  { preserveState: false },
                )
              }
            >
              Refresh suggestions
            </Button>
          </div>

          <div className="mb-2.5 flex items-baseline justify-between">
            <h2 className="text-title font-semibold">Line items</h2>
            <LinkAction
              onClick={() =>
                setLines((prev) => [
                  ...prev,
                  { description: "", kind: "fixed", quantity: "1", sourceId: null, sourceType: null, unitAmount: "0" },
                ])
              }
            >
              Add line
            </LinkAction>
          </div>

          {lines.length === 0 ? (
            <p className="border-t border-border py-8 text-center text-label text-muted-foreground">
              No lines. Add one or widen the labor period.
            </p>
          ) : (
            <DataTable minWidth={720}>
              <Thead>
                <Th>Description</Th>
                <Th>Kind</Th>
                <Th align="right" className="w-16">
                  Qty
                </Th>
                <Th align="right" className="w-24">
                  Unit
                </Th>
                <Th align="right">Amount</Th>
                <Th />
              </Thead>
              <tbody>
                {lines.map((line, index) => (
                  <Tr key={index} className="hover:bg-transparent">
                    <Td className="py-1.5">
                      <Input
                        value={line.description}
                        onChange={(e) => updateLine(index, { description: e.target.value })}
                      />
                    </Td>
                    <Td muted className="py-1.5 text-label capitalize">
                      {line.kind}
                    </Td>
                    <Td className="py-1.5">
                      <Input
                        className="num text-right"
                        type="number"
                        step="0.01"
                        value={line.quantity}
                        onChange={(e) => updateLine(index, { quantity: e.target.value })}
                      />
                    </Td>
                    <Td className="py-1.5">
                      <Input
                        className="num text-right"
                        type="number"
                        step="0.01"
                        value={line.unitAmount}
                        onChange={(e) => updateLine(index, { unitAmount: e.target.value })}
                      />
                    </Td>
                    <Td align="right" num strong className="py-1.5">
                      {formatCurrency(lineAmount(line))}
                    </Td>
                    <Td align="right" className="py-1.5">
                      <Button
                        variant="ghost"
                        size="icon-sm"
                        onClick={() => setLines((prev) => prev.filter((_, i) => i !== index))}
                      >
                        <Trash2 />
                      </Button>
                    </Td>
                  </Tr>
                ))}
              </tbody>
            </DataTable>
          )}
        </div>

        <aside className="px-5 py-4 pb-7">
          <p className="eyebrow mb-2.5">Invoice total</p>
          <p className="num mb-4 text-[28px] font-semibold tracking-[-0.025em] text-whisper">
            {formatCurrency(total)}
          </p>

          <SummaryRow label="Subtotal" value={formatCurrency(subtotal)} />
          <SummaryRow label="Labor" value={formatCurrency(laborTotal)} />
          <SummaryRow label="Expenses" value={formatCurrency(expenseTotal)} />

          <div className="mt-3.5 grid grid-cols-2 gap-2.5">
            <Field label="Tax">
              <Input
                className="num text-right"
                type="number"
                step="0.01"
                value={tax}
                onChange={(e) => setTax(e.target.value)}
              />
            </Field>
            <Field label="Terms (days)">
              <Input
                className="num text-right"
                type="number"
                value={termsDays}
                onChange={(e) => setTermsDays(e.target.value)}
              />
            </Field>
          </div>

          <Field label="Notes" className="mt-2.5">
            <Textarea
              rows={3}
              placeholder="Visible on the invoice"
              value={notes}
              onChange={(e) => setNotes(e.target.value)}
            />
          </Field>

          <div className="mt-4 flex flex-col gap-1.5">
            <Button size="sm" disabled={lines.length === 0} onClick={submit}>
              <Plus />
              Create draft
            </Button>
            <Button variant="outline" size="sm" asChild>
              <Link href={`/projects/${project.id}`}>Cancel</Link>
            </Button>
          </div>
        </aside>
      </div>
    </>
  )
}

function SummaryRow({ label, value }: { label: string; value: string }) {
  return (
    <div className="flex items-center justify-between border-t border-border py-2">
      <span className="text-label text-muted-foreground">{label}</span>
      <span className="num text-body">{value}</span>
    </div>
  )
}
